import { endOfDay, millisecondsToSeconds, startOfDay } from 'date-fns';

import findProgramIndexForUpdate from 'store/epg/utils/findProgramIndexForUpdate';
import { RequestStatus } from 'store/utils';

import {
    getEpgByVisibleChannelsRequest,
    getEpgFailure,
    getEpgRequest,
    getEpgSuccess,
    getPartialUpdateEpgSuccess,
    setEpgDate,
    setEpgLastUpdated,
    setEpgSelectedLayout,
    setFocusToNow,
    setFullscreen,
    setPreviouslyWatchedChannel,
    setSubtitlesDialogVisible,
} from './actions';
import {
    GET_EPG_BY_VISIBLE_CHANNELS_REQUEST,
    GET_EPG_BY_VISIBLE_CHANNELS_SUCCESS,
    GET_EPG_FAILURE,
    GET_EPG_PARTIAL_UPDATE_SUCCESS,
    GET_EPG_REQUEST,
    GET_EPG_SUCCESS,
    SET_EPG_DATE,
    SET_EPG_LAST_UPDATED,
    SET_EPG_REQUEST_IDLE_STATUS,
    SET_EPG_SELECTED_LAYOUT,
    SET_FOCUS_TO_NOW,
    SET_FULLSCREEN,
    SET_PREVIOUSLY_WATCHED_CHANNEL,
    SET_SUBTITLES_DIALOG_VISIBLE,
} from './constants';
import { EpgState } from './reducer.types';

const now = new Date();

export enum EpgRequestedBy {
    SCREEN_ACCESS = 'screen_access',
    DATE_PICKER = 'date_picker',
    PREV_DAY_JUMP_MARKER = 'prev_day_jump_marker',
    NEXT_DAY_JUMP_MARKER = 'next_day_jump_marker',
    NOW_JUMP_MARKER = 'now_jump_marker',
}

const initialState: EpgState = {
    epg: {
        status: RequestStatus.IDLE,
        start: millisecondsToSeconds(startOfDay(now).getTime()),
        end: millisecondsToSeconds(endOfDay(now).getTime()),
        data: {},
        error: undefined,
        requestedBy: EpgRequestedBy.SCREEN_ACCESS,
        newDayIsLoading: true,
    },
    fullscreen: false,
    subtitlesDialogVisible: false,
    focusToNow: true,
    epgLastUpdated: undefined,
    epgSelectedLayout: undefined,
    previouslyWatchedChannelByProfile: {},
};

const handlers = {
    [GET_EPG_REQUEST](
        state: EpgState,
        {
            payload: { start, end, requestedBy },
        }: ReturnType<typeof getEpgRequest>,
    ) {
        return {
            ...state,
            epg: {
                ...state.epg,
                start,
                end,
                status: RequestStatus.PENDING,
                data: {},
                requestedBy,
            },
        };
    },
    [GET_EPG_SUCCESS](
        state: EpgState,
        { payload: { data } }: ReturnType<typeof getEpgSuccess>,
    ): EpgState {
        const dateKey = `${state.epg.start}-${state.epg.end}`;

        return {
            ...state,
            epg: {
                ...state.epg,
                data: {
                    ...state.epg.data,
                    [dateKey]: { ...(state.epg.data[dateKey] || {}), ...data },
                },
                status: RequestStatus.SUCCESS,
                error: undefined,
                newDayIsLoading: false,
            },
        };
    },
    [GET_EPG_FAILURE](
        state: EpgState,
        { payload: { error } }: ReturnType<typeof getEpgFailure>,
    ): EpgState {
        return {
            ...state,
            epg: {
                ...state.epg,
                error,
                data: {},
                status: RequestStatus.FAILURE,
                newDayIsLoading: false,
            },
        };
    },
    [SET_EPG_REQUEST_IDLE_STATUS](state: EpgState): EpgState {
        return {
            ...state,
            epg: {
                ...state.epg,
                status: RequestStatus.IDLE,
            },
        };
    },
    [GET_EPG_BY_VISIBLE_CHANNELS_REQUEST](
        state: EpgState,
        {
            payload: { start, end, requestedBy },
        }: ReturnType<typeof getEpgByVisibleChannelsRequest>,
    ): EpgState {
        const dateKey = `${start}-${end}`;

        return {
            ...state,
            epg: {
                ...state.epg,
                start,
                end,
                requestedBy: requestedBy!,
                newDayIsLoading: !state.epg.data[dateKey],
            },
        };
    },
    [GET_EPG_BY_VISIBLE_CHANNELS_SUCCESS](
        state: EpgState,
        { payload: { data } }: ReturnType<typeof getEpgSuccess>,
    ): EpgState {
        const dateKey = `${state.epg.start}-${state.epg.end}`;

        return {
            ...state,
            epg: {
                ...state.epg,
                data: {
                    ...state.epg.data,
                    [dateKey]: { ...(state.epg.data[dateKey] || {}), ...data },
                },
                status: RequestStatus.SUCCESS,
                error: undefined,
                newDayIsLoading: false,
            },
        };
    },
    [SET_FULLSCREEN](
        state: EpgState,
        { payload: { fullscreen } }: ReturnType<typeof setFullscreen>,
    ) {
        return {
            ...state,
            fullscreen,
        };
    },
    [SET_PREVIOUSLY_WATCHED_CHANNEL](
        state: EpgState,
        {
            payload: { profileId, channelId },
        }: ReturnType<typeof setPreviouslyWatchedChannel>,
    ): EpgState {
        if (profileId) {
            return {
                ...state,
                previouslyWatchedChannelByProfile: {
                    ...state.previouslyWatchedChannelByProfile,
                    [profileId]: channelId,
                },
            };
        }

        return state;
    },

    [SET_EPG_DATE](
        state: EpgState,
        { payload: { start, end, requestedBy } }: ReturnType<typeof setEpgDate>,
    ) {
        return {
            ...state,
            epg: {
                ...state.epg,
                start,
                end,
                requestedBy: requestedBy || EpgRequestedBy.SCREEN_ACCESS,
            },
        };
    },

    [SET_SUBTITLES_DIALOG_VISIBLE](
        state: EpgState,
        {
            payload: { subtitlesDialogVisible },
        }: ReturnType<typeof setSubtitlesDialogVisible>,
    ): EpgState {
        return {
            ...state,
            subtitlesDialogVisible,
        };
    },

    [GET_EPG_PARTIAL_UPDATE_SUCCESS](
        state: EpgState,
        {
            payload: { data, end, start },
        }: ReturnType<typeof getPartialUpdateEpgSuccess>,
    ): EpgState {
        const dateKey = `${state.epg.start}-${state.epg.end}`;

        if (
            // check if the coming data is in epg range
            state.epg.start &&
            state.epg.start <= start &&
            state.epg.end &&
            state.epg.end >= end
        ) {
            const newData = { ...(state.epg.data[dateKey] || {}) };

            Object.keys(data).forEach((channelId) => {
                if (newData[channelId]) {
                    const channel = data[channelId];

                    const {
                        replaceFirstProgram,
                        replaceLastProgram,
                        startIndex,
                        endIndex,
                    } = findProgramIndexForUpdate(channelId, data, newData);

                    if (startIndex !== -1 && endIndex !== -1) {
                        // eslint-disable-next-line @typescript-eslint/no-shadow
                        const start = replaceFirstProgram
                            ? startIndex
                            : startIndex + 1;
                        const itemsToReplace = replaceLastProgram
                            ? endIndex - start + 1
                            : endIndex - start;

                        newData[channelId].splice(
                            start,
                            itemsToReplace,
                            ...channel,
                        );
                    }
                }
            });

            return {
                ...state,
                epg: {
                    ...state.epg,
                    data: {
                        ...state.epg.data,
                        [dateKey]: {
                            ...(state.epg.data[dateKey] || {}),
                            ...newData,
                        },
                    },
                },
            };
        }

        return state;
    },

    [SET_FOCUS_TO_NOW](
        state: EpgState,
        { payload: { focusToNow } }: ReturnType<typeof setFocusToNow>,
    ): EpgState {
        return {
            ...state,
            focusToNow,
        };
    },
    [SET_EPG_LAST_UPDATED](
        state: EpgState,
        { payload: { epgLastUpdated } }: ReturnType<typeof setEpgLastUpdated>,
    ): EpgState {
        return {
            ...state,
            epgLastUpdated,
        };
    },
    [SET_EPG_SELECTED_LAYOUT](
        state: EpgState,
        {
            payload: { epgSelectedLayout },
        }: ReturnType<typeof setEpgSelectedLayout>,
    ): EpgState {
        return {
            ...state,
            epgSelectedLayout,
        };
    },
};

export default { initialState, handlers };
